// Question Key Mapping for Backward Compatibility
// This ensures that user answers remain correctly mapped even if question text, order, or keys change

export interface QuestionMapping {
  id: string;        // Stable identifier (q1, q2, q3, etc.)
  key: string;       // Current question key
  label: string;     // Question text
  required?: boolean; // Whether this question is required for completion
}

export interface MappedAnswer {
  key: string;       // Original question key
  answer: string;    // User's answer
}

export interface QuestionData {
  [questionId: string]: MappedAnswer; // e.g., { "q1": { key: "spirituality_childhood", answer: "..." } }
}

// Step 6 character defects use their own structure and are already encrypted

// Step 2 Question Mappings
export const STEP2_QUESTIONS: QuestionMapping[] = [
  {
    id: "q1",
    key: "spirituality_childhood",
    label: "Describe what spirituality / religion looked like for you growing up, its impact on you, and any feelings you have about it.",
    required: true
  },
  {
    id: "q2", 
    key: "spirituality_now",
    label: "Describe the role, if any, that spirituality / religion has in your life currently.",
    required: true
  },
  {
    id: "q3",
    key: "higher_power", 
    label: "Describe your higher power if you have one (e.g., perfectionist judge, kind grandpa, vending machine, not there)",
    required: true
  },
  {
    id: "q4",
    key: "anger_higher_power",
    label: "Describe any feelings of anger you had or have about a higher power.",
    required: true
  },
  {
    id: "q5",
    key: "prayer_deals",
    label: "Recount any times you have prayed for something or made a deal with your higher power in moments of distress, and what happened after.",
    required: true
  },
  {
    id: "q6",
    key: "authority_abuse",
    label: "If appropriate, describe how someone in your life abused their authority over you and how that impacted your view of a higher power.",
    required: false
  }
];

// Step 3 Question Mappings
export const STEP3_QUESTIONS: QuestionMapping[] = [
  // Group: Decision
  {
    id: "q1",
    key: "decision_maker_while_acting_out",
    label: "Who or what made my decisions for me while I was compulsively sexually acting out?",
    required: true
  },
  {
    id: "q2",
    key: "fears_about_decision",
    label: "What fears do I have about making a decision to turn my will and life over to a higher power of my understanding?",
    required: true
  },
  // Group: Self-will
  {
    id: "q3",
    key: "problems_tried_to_resolve",
    label: "Which problems have I tried to resolve through willpower?",
    required: true
  },
  {
    id: "q4",
    key: "self_will_suffering",
    label: "How has self-will and my reactions to life events made me and others suffer? Be specific.",
    required: true
  },
  // Group: God of my understanding
  {
    id: "q5",
    key: "decision_maker_in_recovery",
    label: "Who or what makes my decisions for me in recovery?",
    required: true
  },
  {
    id: "q6",
    key: "concept_of_higher_power",
    label: "Is my current concept of a higher power still working, and how might I need to change my concept of a higher power?",
    required: true
  },
  // Group: Turning it over
  {
    id: "q7",
    key: "examples_turned_over_to_addiction",
    label: "What are specific examples of how I turned my life and will over to my addiction?",
    required: true
  },
  {
    id: "q8",
    key: "day_if_turned_over",
    label: "How would my day look if I were to turn my will and my life over to the care of a higher power of my understanding?",
    required: true
  },
  {
    id: "q9",
    key: "thoughts_behaviors_willing_to_turn_over",
    label: "Which thoughts and behaviors am I willing to turn over right now, and which am I not yet?",
    required: true
  }
];

// Step 6 uses character defects which have a different structure
// Character defects are already encrypted and don't need question key mapping

// Migration mapping for old data format
export const OLD_TO_NEW_MAPPING = {
  step2: {
    "spirituality_childhood": "q1",
    "spirituality_now": "q2",
    "higher_power": "q3",
    "anger_higher_power": "q4",
    "prayer_deals": "q5",
    "authority_abuse": "q6"
  },
  step3: {
    "decision_maker_while_acting_out": "q1",
    "fears_about_decision": "q2",
    "problems_tried_to_resolve": "q3",
    "self_will_suffering": "q4",
    "decision_maker_in_recovery": "q5",
    "concept_of_higher_power": "q6",
    "examples_turned_over_to_addiction": "q7",
    "day_if_turned_over": "q8",
    "thoughts_behaviors_willing_to_turn_over": "q9"
  },
  // Step 6 character defects don't need migration mapping
};

/**
 * Migrate old question data to new mapped format
 */
export function migrateQuestionData(stepNumber: number, oldData: any): QuestionData {
  if (!oldData) return {};

  const stepKey = `step${stepNumber}` as keyof typeof OLD_TO_NEW_MAPPING;
  const mapping = OLD_TO_NEW_MAPPING[stepKey];

  if (!mapping) return oldData; // No migration needed

  const newData: QuestionData = {};

  Object.entries(oldData).forEach(([key, answer]) => {
    const newId = mapping[key as keyof typeof mapping];
    if (newId && typeof answer === 'string') {
      newData[newId] = { key, answer };
    }
  });

  return newData;
}

// Character defect migration functions removed - Step 6 uses original format

/**
 * Convert new mapped data back to simple key-value format for display
 */
export function unmappedQuestionData(mappedData: QuestionData): Record<string, string> {
  const result: Record<string, string> = {};
  
  Object.entries(mappedData).forEach(([questionId, mappedAnswer]) => {
    result[mappedAnswer.key] = mappedAnswer.answer;
  });
  
  return result;
}

/**
 * Get question mapping for a specific step
 */
export function getQuestionMapping(stepNumber: number): QuestionMapping[] {
  switch (stepNumber) {
    case 2:
      return STEP2_QUESTIONS;
    case 3:
      return STEP3_QUESTIONS;
    // Step 6 character defects don't use question mapping
    default:
      return [];
  }
}

/**
 * Get question by ID
 */
export function getQuestionById(stepNumber: number, questionId: string): QuestionMapping | undefined {
  const questions = getQuestionMapping(stepNumber);
  return questions.find(q => q.id === questionId);
}

/**
 * Get question by key
 */
export function getQuestionByKey(stepNumber: number, key: string): QuestionMapping | undefined {
  const questions = getQuestionMapping(stepNumber);
  return questions.find(q => q.key === key);
}

/**
 * Check if data needs migration
 */
export function needsMigration(data: any): boolean {
  if (!data) return false;
  
  // Check if data is in old format (simple key-value pairs)
  const firstValue = Object.values(data)[0];
  return typeof firstValue === 'string';
}

/**
 * Validate that all required questions are answered
 */
export function validateRequiredQuestions(stepNumber: number, data: QuestionData): boolean {
  const questions = getQuestionMapping(stepNumber);
  const requiredQuestions = questions.filter(q => q.required);
  
  return requiredQuestions.every(question => {
    const answer = data[question.id];
    return answer && answer.answer && answer.answer.trim().length > 0;
  });
} 